import { computed } from "vue";
import { TestId } from "#/test-ids";
import { useOmnisearchStore } from "#ce/apps/shared/store/omnisearch-store";
import { useOmnisearchModels } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchModels";
import { useOmnisearchSuggestions } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchSuggestions";

const TEST_IDS = Object.freeze({
  input: TestId.finder.searchInput,
  sheetInput: TestId.finder.sheetSearchInput,
  item: TestId.finder.suggestion,
  clear: TestId.finder.clear,
  sheetClear: TestId.finder.sheetClear,
});

/**
 * Returns the provider omnisearch's test ids, field wording, item models, and the
 * handlers that report each change to the wizard as an event. The data comes
 * from the shared omnisearch store.
 *
 * @param {Object} options
 * @param {string} [options.uuid] - the doctor-finder embed uuid that selects the store instance
 * @param {Function} options.emit - the root's `emit` for `select`, `clear`, `text-change` and `submit`
 * @param {import('vue').Ref<string>} options.searchQuery - the debounced query
 * @param {Object} options.selection - the root's `useOmnisearchSelection` result
 * @param {(text: string) => void} options.setText - writes the field text without re-triggering a search
 */
export function useProviderOmnisearch({ uuid, emit, searchQuery, selection, setText }) {
  const store = useOmnisearchStore(uuid);

  const placeholder = computed(() => store.config.value?.placeholder ?? "");
  const ariaLabel = computed(() => store.config.value?.ariaLabel ?? "");

  // True during the store's first load, so an early open shows the picker's spinner, not "No data".
  const loading = store.loading;

  const {
    suggestion,
    request: requestSuggestion,
    clear: clearSuggestion,
  } = useOmnisearchSuggestions({
    store,
    searchQuery,
  });

  const { models } = useOmnisearchModels({ store, searchQuery, suggestion });

  function changeText(text) {
    selection.selected.value = null;
    selection.clearCustomValidity();
    requestSuggestion(text.trim());
    // Surface the typed free-text so the wizard can keep its `query` filter in
    // sync — a search submitted without picking a suggestion still runs it.
    emit("text-change", text);
  }

  // Commit a chosen row: remember it (required-skill validation reads
  // `hasSelection`), echo its text into the field, and surface the routing
  // payload. `selectionText` overrides the display title for rows whose label
  // decorates the committed text (the quoted phrase-search row).
  function commitItem(item) {
    selection.selected.value = item;
    setText(item.selectionText ?? item.title ?? "");
    selection.clearCustomValidity();

    emit("select", { title: item.title, ...(item.payload ?? {}) });
  }

  function submitText() {
    emit("submit");
  }

  function clear() {
    clearSuggestion();
    selection.selected.value = null;
    emit("clear");
  }

  return {
    testIds: TEST_IDS,
    placeholder,
    ariaLabel,
    loading,
    models,
    changeText,
    commitItem,
    submitText,
    clear,
  };
}
